/* My Shopstop — SS-LISTS v1.0 (V1140)
 * Merkliste (wishlist) + Produktvergleich (compare) without any app.
 *
 * - State lives only in the visitor's browser (localStorage), per list:
 *     ss:wishlist:v1  max 100 products
 *     ss:compare:v1   max 4 products
 *   Item = { id, handle, title, img, t }  (product id, handle, title, small image URL, time added)
 * - Custom elements upgrade themselves wherever their markup appears, also in cards
 *   that are inserted later by AJAX (recommendations, recently viewed, wishlist page):
 *     <ss-list-toggle list="wishlist|compare" ...>  button on cards and on the PDP
 *     <ss-list-count list="...">                    counter badge (header)
 *     <ss-compare-tray>                             bottom bar while comparing
 *     <ss-wishlist-page>                            content of /pages/merkliste
 *     <ss-compare-page>                             content of /pages/produktvergleich
 * - Every tab stays in sync (storage event). Nothing is sent to any server; product
 *   data for the two pages is read from the shop with normal storefront requests.
 * - All text from the shop is inserted with textContent (never innerHTML), except the
 *   theme's own card markup returned by the Section Rendering API.
 */
(() => {
  'use strict';
  if (window.SSLists) return;

  const LISTS = {
    wishlist: { key: 'ss:wishlist:v1', max: 100, newestFirst: true },
    compare: { key: 'ss:compare:v1', max: 4, newestFirst: false }
  };
  const I18N = window.SSListsI18n || {};
  const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const HANDLE_RE = /^[^\s/?#]{1,255}$/;

  function t(key, vars) {
    let s = I18N[key] || key;
    if (vars) Object.keys(vars).forEach((k) => { s = s.split('{' + k + '}').join(String(vars[k])); });
    return s;
  }

  /* ---------------- storage ---------------- */
  function valid(item) {
    return item && /^\d{1,20}$/.test(String(item.id)) && typeof item.handle === 'string' && HANDLE_RE.test(item.handle);
  }
  function read(list) {
    const cfg = LISTS[list];
    if (!cfg) return [];
    try {
      const raw = JSON.parse(window.localStorage.getItem(cfg.key) || '[]');
      return Array.isArray(raw) ? raw.filter(valid).slice(0, cfg.max) : [];
    } catch (e) {
      return [];
    }
  }
  function write(list, items) {
    try {
      window.localStorage.setItem(LISTS[list].key, JSON.stringify(items));
    } catch (e) { /* private mode / quota: the page keeps working, the list is not kept */ }
    emit(list);
  }
  function emit(list) {
    document.dispatchEvent(new CustomEvent('ss:lists:change', { detail: { list: list, items: read(list) } }));
  }
  function has(list, id) {
    return read(list).some((x) => String(x.id) === String(id));
  }
  function add(list, item) {
    if (!valid(item)) return 'invalid';
    const items = read(list);
    if (items.some((x) => String(x.id) === String(item.id))) return 'exists';
    if (items.length >= LISTS[list].max) return 'full';
    const entry = {
      id: String(item.id),
      handle: item.handle,
      title: String(item.title || '').slice(0, 200),
      img: String(item.img || '').slice(0, 600),
      t: Date.now()
    };
    // Merkliste: newest first. Vergleich: in the order chosen (left to right).
    if (LISTS[list].newestFirst) items.unshift(entry); else items.push(entry);
    write(list, items);
    track(list + '_add', item);
    return 'added';
  }
  function remove(list, id) {
    const items = read(list);
    const next = items.filter((x) => String(x.id) !== String(id));
    if (next.length !== items.length) {
      write(list, next);
      track(list + '_remove', { id: id });
    }
  }
  function clear(list) {
    write(list, []);
  }
  window.addEventListener('storage', (e) => {
    Object.keys(LISTS).forEach((list) => { if (e.key === LISTS[list].key) emit(list); });
  });

  /* ---------------- analytics (Shopify Customer Events) ---------------- */
  function track(name, item) {
    try {
      if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
        window.Shopify.analytics.publish('ss_lists:' + name, { product_id: String(item.id || ''), handle: String(item.handle || '') });
      }
    } catch (e) { /* analytics never breaks the button */ }
  }

  /* ---------------- live region + toast ---------------- */
  let live = null;
  let toastTimer = 0;
  function announce(message) {
    if (!live) {
      live = document.createElement('div');
      live.className = 'ss-lists-toast';
      live.setAttribute('role', 'status');
      live.setAttribute('aria-live', 'polite');
      document.body.appendChild(live);
    }
    live.textContent = message;
    live.classList.add('is-visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => live.classList.remove('is-visible'), 2600);
  }

  /* ---------------- <ss-list-toggle> ---------------- */
  class SSListToggle extends HTMLElement {
    connectedCallback() {
      this.list = this.getAttribute('list');
      this.button = this.querySelector('button');
      if (!LISTS[this.list] || !this.button) return;
      this.onClick = this.onClick.bind(this);
      this.sync = this.sync.bind(this);
      this.button.addEventListener('click', this.onClick);
      document.addEventListener('ss:lists:change', this.sync);
      this.sync();
    }
    disconnectedCallback() {
      if (this.button) this.button.removeEventListener('click', this.onClick);
      document.removeEventListener('ss:lists:change', this.sync);
    }
    item() {
      return {
        id: this.dataset.productId,
        handle: this.dataset.handle,
        title: this.dataset.title,
        img: this.dataset.img
      };
    }
    sync(e) {
      if (e && e.detail && e.detail.list !== this.list) return;
      const on = has(this.list, this.dataset.productId);
      this.button.setAttribute('aria-pressed', on ? 'true' : 'false');
      const label = on ? this.dataset.labelOn : this.dataset.labelOff;
      if (label) {
        this.button.setAttribute('aria-label', label);
        this.button.setAttribute('data-tooltip', label);
        const text = this.querySelector('[data-ss-list-text]');
        if (text) text.textContent = label;
      }
      this.classList.toggle('is-active', on);
    }
    onClick(e) {
      e.preventDefault();
      e.stopPropagation();
      const item = this.item();
      if (has(this.list, item.id)) {
        remove(this.list, item.id);
        announce(t(this.list + '_removed'));
        return;
      }
      const res = add(this.list, item);
      if (res === 'added') announce(t(this.list + '_added'));
      else if (res === 'full') announce(t(this.list + '_full', { max: LISTS[this.list].max }));
    }
  }

  /* ---------------- <ss-list-count> ---------------- */
  class SSListCount extends HTMLElement {
    connectedCallback() {
      this.list = this.getAttribute('list');
      this.sync = this.sync.bind(this);
      document.addEventListener('ss:lists:change', this.sync);
      this.sync();
    }
    disconnectedCallback() {
      document.removeEventListener('ss:lists:change', this.sync);
    }
    sync(e) {
      if (e && e.detail && e.detail.list !== this.list) return;
      const n = read(this.list).length;
      this.textContent = n > 99 ? '99+' : String(n);
      this.hidden = false;
    }
  }

  /* ---------------- <ss-compare-tray> ---------------- */
  class SSCompareTray extends HTMLElement {
    connectedCallback() {
      this.render = this.render.bind(this);
      document.addEventListener('ss:lists:change', this.render);
      this.render();
    }
    disconnectedCallback() {
      document.removeEventListener('ss:lists:change', this.render);
    }
    render(e) {
      if (e && e.detail && e.detail.list !== 'compare') return;
      const items = read('compare');
      const onComparePage = Boolean(document.querySelector('ss-compare-page'));
      this.hidden = items.length === 0 || onComparePage;
      document.documentElement.classList.toggle('ss-compare-tray-open', !this.hidden);
      this.replaceChildren();
      if (this.hidden) return;

      const bar = el('div', 'ss-compare-tray__bar');
      bar.setAttribute('role', 'region');
      bar.setAttribute('aria-label', t('compare_tray'));
      const list = el('ul', 'ss-compare-tray__items');
      for (let i = 0; i < LISTS.compare.max; i++) {
        const it = items[i];
        const li = el('li', 'ss-compare-tray__item' + (it ? '' : ' is-empty'));
        if (it) {
          if (it.img) {
            const img = el('img');
            img.src = it.img;
            img.alt = '';
            img.width = 48;
            img.height = 48;
            img.loading = 'lazy';
            li.appendChild(img);
          }
          const x = el('button', 'ss-compare-tray__remove');
          x.type = 'button';
          x.setAttribute('aria-label', t('compare_remove_item', { title: it.title || it.handle }));
          x.textContent = '×';
          x.addEventListener('click', () => remove('compare', it.id));
          li.title = it.title || '';
          li.appendChild(x);
        }
        list.appendChild(li);
      }
      const actions = el('div', 'ss-compare-tray__actions');
      const go = el('a', 'ss-compare-tray__go');
      go.href = this.dataset.url || (ROOT + 'pages/produktvergleich');
      go.textContent = t('compare_now', { n: items.length });
      if (items.length < 2) {
        go.setAttribute('aria-disabled', 'true');
        go.addEventListener('click', (ev) => { ev.preventDefault(); announce(t('compare_need_two')); });
      }
      const clr = el('button', 'ss-compare-tray__clear');
      clr.type = 'button';
      clr.textContent = t('compare_clear');
      clr.addEventListener('click', () => clear('compare'));
      actions.append(go, clr);
      bar.append(list, actions);
      this.appendChild(bar);
    }
  }

  /* ---------------- helpers for the two pages ---------------- */
  function el(tag, cls) {
    const n = document.createElement(tag);
    if (cls) n.className = cls;
    return n;
  }
  function productUrl(handle, query) {
    return ROOT + 'products/' + encodeURIComponent(handle) + (query ? '?' + query : '');
  }
  async function pool(items, size, fn) {
    const out = new Array(items.length);
    let next = 0;
    async function worker() {
      while (next < items.length) {
        const i = next++;
        try { out[i] = await fn(items[i]); } catch (e) { out[i] = null; }
      }
    }
    await Promise.all(Array.from({ length: Math.min(size, items.length) }, worker));
    return out;
  }
  async function fetchText(url) {
    const r = await fetch(url, { credentials: 'same-origin' });
    if (r.status === 404) return { gone: true };
    if (!r.ok) throw new Error('HTTP ' + r.status);
    return { text: await r.text() };
  }

  /* ---------------- <ss-wishlist-page> ---------------- */
  class SSWishlistPage extends HTMLElement {
    connectedCallback() {
      this.grid = this.querySelector('[data-ss-wl-grid]');
      this.empty = this.querySelector('[data-ss-wl-empty]');
      this.count = this.querySelector('[data-ss-wl-count]');
      this.clearBtn = this.querySelector('[data-ss-wl-clear]');
      this.onChange = this.onChange.bind(this);
      document.addEventListener('ss:lists:change', this.onChange);
      if (this.clearBtn) {
        this.clearBtn.addEventListener('click', () => {
          if (window.confirm(t('wishlist_clear_confirm'))) clear('wishlist');
        });
      }
      this.load();
    }
    disconnectedCallback() {
      document.removeEventListener('ss:lists:change', this.onChange);
    }
    state(n) {
      if (this.empty) this.empty.hidden = n > 0;
      if (this.clearBtn) this.clearBtn.hidden = n === 0;
      if (this.count) this.count.textContent = n ? (n === 1 ? t('wishlist_count_one') : t('wishlist_count', { n: n })) : '';
      this.setAttribute('aria-busy', 'false');
    }
    async load() {
      const items = read('wishlist');
      this.state(items.length);
      if (!items.length || !this.grid) return;
      this.setAttribute('aria-busy', 'true');
      const section = this.dataset.section || 'ss-wishlist-product-item';
      const results = await pool(items, 6, (it) => fetchText(productUrl(it.handle, 'section_id=' + encodeURIComponent(section))));
      const frag = document.createDocumentFragment();
      const gone = [];
      results.forEach((res, i) => {
        if (!res) return;
        if (res.gone) { gone.push(items[i].id); return; }
        const doc = new DOMParser().parseFromString(res.text, 'text/html');
        const wrap = doc.getElementById('shopify-section-' + section);
        const card = wrap && wrap.querySelector('li');
        if (!card) return;
        card.setAttribute('data-ss-list-id', items[i].id);
        frag.appendChild(document.importNode(card, true));
      });
      this.grid.replaceChildren(frag);
      gone.forEach((id) => remove('wishlist', id)); // product deleted or unpublished
      this.state(read('wishlist').length);
    }
    onChange(e) {
      if (!e.detail || e.detail.list !== 'wishlist' || !this.grid) return;
      const ids = new Set(e.detail.items.map((x) => String(x.id)));
      this.grid.querySelectorAll('[data-ss-list-id]').forEach((card) => {
        if (!ids.has(card.getAttribute('data-ss-list-id'))) card.remove();
      });
      const shown = this.grid.querySelectorAll('[data-ss-list-id]').length;
      if (e.detail.items.length > shown) this.load(); // added in another tab
      else this.state(e.detail.items.length);
    }
  }

  /* ---------------- <ss-compare-page> ---------------- */
  class SSComparePage extends HTMLElement {
    connectedCallback() {
      this.table = this.querySelector('[data-ss-cmp-table]');
      this.empty = this.querySelector('[data-ss-cmp-empty]');
      this.diff = this.querySelector('[data-ss-cmp-diff]');
      this.data = [];
      this.onChange = this.onChange.bind(this);
      document.addEventListener('ss:lists:change', this.onChange);
      if (this.diff) this.diff.addEventListener('change', () => this.draw());
      this.load();
    }
    disconnectedCallback() {
      document.removeEventListener('ss:lists:change', this.onChange);
    }
    async load() {
      const items = read('compare');
      if (!items.length) { this.data = []; this.draw(); return; }
      this.setAttribute('aria-busy', 'true');
      const res = await pool(items, 4, async (it) => {
        const r = await fetchText(productUrl(it.handle, 'view=ss-compare'));
        if (r.gone) return { gone: true, id: it.id };
        return JSON.parse(r.text);
      });
      res.forEach((r) => { if (r && r.gone) remove('compare', r.id); });
      this.data = res.filter((r) => r && !r.gone && r.id);
      this.setAttribute('aria-busy', 'false');
      this.draw();
    }
    onChange(e) {
      if (!e.detail || e.detail.list !== 'compare') return;
      const ids = e.detail.items.map((x) => String(x.id));
      const known = new Set(this.data.map((d) => String(d.id)));
      if (ids.some((id) => !known.has(id))) { this.load(); return; }
      this.data = this.data.filter((d) => ids.indexOf(String(d.id)) !== -1);
      this.draw();
    }
    rows() {
      const rows = [
        { label: t('cmp_brand'), cells: this.data.map((p) => p.vendor || '–') },
        { label: t('cmp_type'), cells: this.data.map((p) => p.type || '–') },
        { label: t('cmp_price'), cells: this.data.map((p) => p.price || '–') },
        { label: t('cmp_stock'), cells: this.data.map((p) => p.stock || '–') }
      ];
      // Union of specification names. The product with the longest list sets the
      // order (and its section headings); names only others have are appended.
      const order = [];
      const seen = new Map();
      this.data.slice().sort((a, b) => (b.specs || []).length - (a.specs || []).length).forEach((p) => (p.specs || []).forEach((s) => {
        const name = String(s.n || '').trim();
        if (!name) return;
        const key = (s.v === '' ? '#' : '') + name.toLowerCase();
        if (!seen.has(key)) { seen.set(key, { name: name, heading: s.v === '' }); order.push(key); }
      }));
      order.forEach((key) => {
        const meta = seen.get(key);
        if (meta.heading) { rows.push({ heading: meta.name }); return; }
        rows.push({
          label: meta.name,
          cells: this.data.map((p) => {
            const hit = (p.specs || []).find((s) => s.v !== '' && String(s.n || '').trim().toLowerCase() === key);
            return hit ? String(hit.v) : '–';
          })
        });
      });
      return rows;
    }
    draw() {
      const n = this.data.length;
      if (this.empty) this.empty.hidden = n > 0;
      if (!this.table) return;
      this.table.hidden = n === 0;
      this.table.replaceChildren();
      if (!n) return;
      this.table.style.setProperty('--ss-cmp-cols', String(n));
      const onlyDiff = Boolean(this.diff && this.diff.checked);

      const thead = el('thead');
      const hr = el('tr');
      const corner = el('th');
      corner.scope = 'col';
      corner.className = 'ss-cmp__corner';
      hr.appendChild(corner);
      this.data.forEach((p) => {
        const th = el('th', 'ss-cmp__product');
        th.scope = 'col';
        const a = el('a', 'ss-cmp__link');
        a.href = p.url;
        if (p.image) {
          const img = el('img', 'ss-cmp__img');
          img.src = p.image;
          img.alt = '';
          img.width = 160;
          img.height = 160;
          img.loading = 'lazy';
          a.appendChild(img);
        }
        const name = el('span', 'ss-cmp__name');
        if (p.vendor) { const b = el('b'); b.textContent = p.vendor; name.append(b, ' '); }
        name.append(p.name || p.title || '');
        a.appendChild(name);
        const x = el('button', 'ss-cmp__remove');
        x.type = 'button';
        x.setAttribute('aria-label', t('compare_remove_item', { title: p.title || '' }));
        x.textContent = '×';
        x.addEventListener('click', () => remove('compare', p.id));
        th.append(x, a);
        hr.appendChild(th);
      });
      thead.appendChild(hr);

      const tbody = el('tbody');
      let pendingHeading = null;
      this.rows().forEach((r) => {
        if (r.heading) { pendingHeading = r.heading; return; }
        const same = r.cells.every((c) => c === r.cells[0]);
        if (onlyDiff && same && n > 1) return;
        if (pendingHeading) {
          const htr = el('tr', 'ss-cmp__group');
          const hth = el('th');
          hth.scope = 'colgroup';
          hth.colSpan = n + 1;
          hth.textContent = pendingHeading;
          htr.appendChild(hth);
          tbody.appendChild(htr);
          pendingHeading = null;
        }
        const tr = el('tr', same ? '' : 'is-different');
        const th = el('th');
        th.scope = 'row';
        th.textContent = r.label;
        tr.appendChild(th);
        r.cells.forEach((c) => { const td = el('td'); td.textContent = c; tr.appendChild(td); });
        tbody.appendChild(tr);
      });
      this.table.append(thead, tbody);
    }
  }

  const define = (name, cls) => { if (!customElements.get(name)) customElements.define(name, cls); };
  define('ss-list-toggle', SSListToggle);
  define('ss-list-count', SSListCount);
  define('ss-compare-tray', SSCompareTray);
  define('ss-wishlist-page', SSWishlistPage);
  define('ss-compare-page', SSComparePage);

  window.SSLists = { read: read, add: add, remove: remove, clear: clear, has: has, version: '1.0' };
})();
